import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { QUERY_KEYS } from '@/lib/constants';
import type { ApiResponse, Book, BookChapter, BookProgress } from './types';

export function useBooks() {
  return useQuery({
    queryKey: QUERY_KEYS.books,
    queryFn: () => api.get<ApiResponse<Book[]>>('/api/v1/books'),
    select: (res) => res.data,
  });
}

export function useBook(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.book(id),
    queryFn: () => api.get<ApiResponse<Book>>(`/api/v1/books/${id}`),
    select: (res) => res.data,
    enabled: !!id,
  });
}

export function useChapters(bookId: string) {
  return useQuery({
    queryKey: QUERY_KEYS.chapters(bookId),
    queryFn: () => api.get<ApiResponse<BookChapter[]>>(`/api/v1/books/${bookId}/chapters`),
    select: (res) => res.data,
    enabled: !!bookId,
  });
}

export function useCreateBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { title: string; short_name?: string; notes?: string }) =>
      api.post('/api/v1/books', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.books }),
  });
}

export function useUpdateBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; title?: string; short_name?: string; notes?: string }) =>
      api.patch(`/api/v1/books/${id}`, body),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.books });
      qc.invalidateQueries({ queryKey: QUERY_KEYS.book(variables.id) });
    },
  });
}

export function useBookProgress(bookId: string | null) {
  return useQuery({
    queryKey: [...QUERY_KEYS.book(bookId!), 'progress'],
    queryFn: () => api.get<ApiResponse<BookProgress>>(`/api/v1/books/${bookId}/progress`),
    select: (res) => res.data,
    enabled: !!bookId,
  });
}
